import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { encabezadoTieneDatos } from './reporteEncabezadoApi';
import { drawEncabezadoReportePdf } from './reporteEncabezadoPdf';
import {
  flattenNestedEncabezadoApiShape,
  groupPopularidadInDisplayOrder,
  mergePopularidadEncabezado,
  normalizePopularidadRow,
  POPULARIDAD_COLUMN_LABELS,
  sumGroupTotals,
} from './popularidadReportModel';

const fmt = (n, maxFrac = 2) =>
  n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('es-MX', { maximumFractionDigits: maxFrac });

const fmtMoney = (n) =>
  n == null || Number.isNaN(n)
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

function rowBodyCells(r, incluirPrecioMenu = true) {
  const cells = [
    r.codigo,
    r.nombre,
    fmt(r.cantidad),
    fmtMoney(r.costo),
    fmtMoney(r.tCosto),
  ];
  if (incluirPrecioMenu) cells.push(fmtMoney(r.precio));
  cells.push(fmtMoney(r.importe), fmtMoney(r.utilidad));
  return cells;
}

/**
 * PDF “formato reporte” como documento legacy: encabezado + bloques por familia + totales + pie bebidas.
 * @param {object[]} rawRows filas crudas (misma orden que pantalla, p. ej. tablaRows)
 * @param {{ fechaInicio?: string, fechaFin?: string, cajaInicio?: number, cajaFin?: number, turnoInicio?: number, turnoFin?: number, cajaDescripcion?: string, turnoDescripcion?: string, fileBase?: string, downloadFileName?: string, encabezado?: object|null, encabezadoFallback?: object, sucursalNombre?: string, reporteTitulo?: string, incluirCortesias?: boolean, bebidasAlcoholicas?: boolean, footer?: { subtotal?: number|null } }} [options]
 */
export function downloadPopularidadPdf(rawRows, options = {}) {
  if (!Array.isArray(rawRows) || rawRows.length === 0) return;

  const {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    cajaDescripcion,
    turnoDescripcion,
    fileBase = 'popularidad',
    downloadFileName,
    encabezado: encabezadoApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo = 'REPORTE POPULARIDAD DE PRODUCTOS',
    incluirCortesias = false,
    bebidasAlcoholicas = false,
    incluirPrecioMenu = true,
    footer = {},
  } = options;

  const normalized = rawRows.map(normalizePopularidadRow).filter(Boolean);
  if (!normalized.length) return;

  const groups = groupPopularidadInDisplayOrder(normalized);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;

  const encApiPrepared =
    encabezadoApi != null && typeof encabezadoApi === 'object'
      ? flattenNestedEncabezadoApiShape(encabezadoApi)
      : encabezadoApi;
  let encMerged = mergePopularidadEncabezado(encApiPrepared, encabezadoFallback, {
    reporteTitulo,
    incluirCortesias,
    bebidasAlcoholicas,
  });
  if (!encabezadoTieneDatos(encMerged)) {
    encMerged = { ...encMerged, reporteTitulo };
  }

  let y = margin;
  const sn = String(sucursalNombre || '').trim();
  const ncUp = String(encMerged.nombreComercial || '').toUpperCase();
  if (sn && !ncUp.includes(sn.toUpperCase())) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(sn.toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 5;
    doc.setTextColor(0);
  }

  y = drawEncabezadoReportePdf(doc, encMerged, pageW, margin, y, {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    cajaDescripcion,
    turnoDescripcion,
  });

  const colLabels = incluirPrecioMenu
    ? POPULARIDAD_COLUMN_LABELS
    : POPULARIDAD_COLUMN_LABELS.filter((h) => h !== 'Precio');
  const head = [colLabels];
  /* jsPDF 4 + autotable: 'auto' puede dejar NaN y romper doc.rect() */
  const tableInnerMm = pageW - margin * 2;
  const fixedColsMm = incluirPrecioMenu
    ? 24 + 20 + 22 + 24 + 22 + 26 + 26
    : 24 + 20 + 22 + 24 + 26 + 26;
  const nombreColMm = Math.max(36, tableInnerMm - fixedColsMm);
  const columnStyles = incluirPrecioMenu
    ? {
        0: { cellWidth: 24, halign: 'left' },
        1: { cellWidth: nombreColMm, halign: 'left' },
        2: { halign: 'right', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 24 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'right', cellWidth: 26 },
        7: { halign: 'right', cellWidth: 26 },
      }
    : {
        0: { cellWidth: 24, halign: 'left' },
        1: { cellWidth: nombreColMm, halign: 'left' },
        2: { halign: 'right', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 24 },
        5: { halign: 'right', cellWidth: 26 },
        6: { halign: 'right', cellWidth: 26 },
      };

  const ensureSpace = (needMm) => {
    const ph = doc.internal.pageSize.getHeight();
    if (y + needMm > ph - 18) {
      doc.addPage();
      y = margin;
    }
  };

  for (const [familia, items] of groups) {
    ensureSpace(28);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(30, 41, 59);
    doc.text(String(familia || 'Sin categoría').toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 6;
    doc.setTextColor(0);

    autoTable(doc, {
      startY: y,
      head,
      body: items.map((r) => rowBodyCells(r, incluirPrecioMenu)),
      theme: 'plain',
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [226, 232, 240],
        fontStyle: 'bold',
        halign: 'center',
      },
      bodyStyles: { textColor: [15, 23, 42], fontSize: 7.5 },
      columnStyles,
      styles: { fontSize: 7.5, cellPadding: 1.4, lineColor: [200, 200, 200], lineWidth: 0.1 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 2;

    const sub = sumGroupTotals(items);
    autoTable(doc, {
      startY: y,
      body: [
        [
          {
            content: `Total ${familia}`,
            colSpan: 2,
            styles: {
              fontStyle: 'bold',
              halign: 'left',
              fillColor: [241, 245, 249],
              textColor: [15, 23, 42],
            },
          },
          {
            content: fmt(sub.cantidad),
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] },
          },
          { content: '', styles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
          {
            content: fmtMoney(sub.tCosto),
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] },
          },
          ...(incluirPrecioMenu ? [{ content: '', styles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] } }] : []),
          {
            content: fmtMoney(sub.importe),
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] },
          },
          {
            content: fmtMoney(sub.utilidad),
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] },
          },
        ],
      ],
      theme: 'plain',
      columnStyles,
      styles: { fontSize: 7.5, cellPadding: 1.4 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  const grand = sumGroupTotals(normalized);
  ensureSpace(22);
  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    body: [
      [
        {
          content: 'Total Global',
          colSpan: 2,
          styles: {
            fontStyle: 'bold',
            halign: 'left',
            fillColor: [226, 232, 240],
            textColor: [15, 23, 42],
          },
        },
        {
          content: fmt(grand.cantidad),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [226, 232, 240], textColor: [15, 23, 42] },
        },
        { content: '', styles: { fillColor: [226, 232, 240], textColor: [15, 23, 42] } },
        {
          content: fmtMoney(grand.tCosto),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [226, 232, 240], textColor: [15, 23, 42] },
        },
        ...(incluirPrecioMenu ? [{ content: '', styles: { fillColor: [226, 232, 240], textColor: [15, 23, 42] } }] : []),
        {
          content: fmtMoney(grand.importe),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [226, 232, 240], textColor: [15, 23, 42] },
        },
        {
          content: fmtMoney(grand.utilidad),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [226, 232, 240], textColor: [15, 23, 42] },
        },
      ],
    ],
    theme: 'plain',
    columnStyles,
    styles: { fontSize: 8, cellPadding: 1.6 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (bebidasAlcoholicas && grand.impuestoBA != null && grand.impuestoBA !== 0) {
    ensureSpace(12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Impuesto bebidas alcohólicas: ${fmtMoney(grand.impuestoBA)}`, pageW - margin, y, { align: 'right' });
    y += 5;
  }
  const subtotal = Number(footer?.subtotal);
  if (bebidasAlcoholicas && Number.isFinite(subtotal)) {
    ensureSpace(12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Subtotal: ${fmtMoney(subtotal)}`, pageW - margin, y, { align: 'right' });
    y += 6;
  }

  const totalPages = doc.internal.getNumberOfPages();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text(`Generado ${new Date().toLocaleString('es-MX')}`, margin, ph - 8);
    doc.text(`Página ${i} de ${totalPages}`, pageW - margin, ph - 8, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(
    downloadFileName ||
      `${fileBase}-${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`
  );
}
